import React, { useState, useCallback, useMemo } from "react";
import { scaleTime, scaleLinear } from "@visx/scale";
import { LinePath } from "@visx/shape";
import { AxisLeft, AxisBottom } from "@visx/axis";
import { extent, max, bisector } from "d3-array";
import { Group } from "@visx/group";
import { GridRows, GridColumns } from "@visx/grid";
import { defaultStyles } from "@visx/tooltip";
import { localPoint } from "@visx/event";
import { useTooltip, TooltipWithBounds } from "@visx/tooltip";
import { LinearGradient } from "@visx/gradient";

const Chart = ({
  userData = [],
  timeFilter,
  isLoading,
  error,
  className = "",
}) => {
  const [size, setSize] = useState(400);

  React.useEffect(() => {
    const handleResize = () => {
      const element = document.getElementById("chart-container");
      if (element) setSize(element.offsetWidth);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const width = size;
  const height = Math.max(220, Math.min(width * 0.5, 400));
  const margin = { top: 40, right: 30, bottom: 50, left: 60 };
  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  const data = useMemo(() => 
    userData?.map(d => ({
      date: new Date(d.date),
      count: d.count
    })).sort((a, b) => a.date - b.date) || [], 
    [userData]
  );

  const currentCount = data[data.length - 1]?.count;

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const xScale = useMemo(() => 
    scaleTime({
      domain: extent(data, d => d.date),
      range: [0, xMax],
      padding: 0.1,
    }), 
    [data, xMax]
  );

  const yScale = useMemo(() =>
    scaleLinear({
      domain: [0, max(data, d => d.count) * 1.1],
      range: [yMax, 0],
      nice: true,
    }),
    [data, yMax]
  );

  const handleTooltip = useCallback((event) => {
    const { x } = localPoint(event) || { x: 0 };
    const x0 = xScale.invert(x - margin.left);
    const index = bisector(d => d.date).left(data, x0, 1);
    const d0 = data[index - 1];
    const d1 = data[index];
    const d = d1 && d1.date && x0 - d0.date > d1.date - x0 ? d1 : d0;
    
    showTooltip({
      tooltipData: d,
      tooltipLeft: xScale(d.date),
      tooltipTop: yScale(d.count),
    });
  }, [showTooltip, xScale, yScale, data, margin.left]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full h-64 bg-gray-800 rounded-lg">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data.length) {
    return (
      <div className="flex items-center justify-center w-full h-64 bg-gray-800 rounded-lg">
        <span className="text-gray-400">{error || "No data available"}</span>
      </div>
    );
  }

  return (
    <div id="chart-container" className={`relative flex flex-col items-center ${className}`} style={{ width: "100%", height }}>
      <div className="text-2xl font-bold text-blue-400 text-center">
        {currentCount?.toLocaleString() || "0"}
      </div>

      <svg width={width} height={height}>
        <LinearGradient id="area-gradient" from="#667EEA" to="#667EEA" fromOpacity={0.4} toOpacity={0.1} />
        <rect x={0} y={0} width={width} height={height} fill="#262626" rx={14} />

        <Group left={margin.left} top={margin.top}>
          <GridRows scale={yScale} width={xMax} height={yMax} stroke="#333333" strokeOpacity={0.2} numTicks={5} />
          <GridColumns scale={xScale} width={xMax} height={yMax} stroke="#333333" strokeOpacity={0.2} numTicks={width > 520 ? 10 : 5} />

          <path
            d={`
              M${xScale(data[0].date)},${yScale(data[0].count)}
              ${data.map(d => `L${xScale(d.date)},${yScale(d.count)}`).join("")}
              L${xScale(data[data.length - 1].date)},${yMax}
              L${xScale(data[0].date)},${yMax}
              Z
            `}
            fill="url(#area-gradient)"
          />

          <LinePath
            data={data}
            x={d => xScale(d.date)}
            y={d => yScale(d.count)}
            stroke="#667EEA"
            strokeWidth={2}
            strokeOpacity={0.8}
          />

          <AxisLeft
            scale={yScale}
            stroke="#666666"
            tickStroke="#666666"
            tickLabelProps={() => ({
              fill: "#999999",
              fontSize: 11,
              textAnchor: "end",
              dx: -8,
              dy: 4,
            })}
          />

          <AxisBottom
            top={yMax}
            scale={xScale}
            stroke="#666666"
            tickStroke="#666666"
            numTicks={width > 520 ? 10 : 5}
            tickLabelProps={() => ({
              fill: "#999999",
              fontSize: 11,
              textAnchor: "middle",
              dy: 10,
            })}
          />

          <rect
            x={0}
            y={0}
            width={xMax}
            height={yMax}
            fill="transparent"
            onMouseMove={handleTooltip}
            onMouseLeave={hideTooltip}
          />

          {tooltipData && (
            <>
              <line x1={tooltipLeft} x2={tooltipLeft} y1={0} y2={yMax} stroke="#667EEA" strokeWidth={1} strokeDasharray="4,4" />
              <circle cx={tooltipLeft} cy={tooltipTop} r={4} fill="#667EEA" stroke="white" strokeWidth={2} />
            </>
          )}
        </Group>
      </svg>

      {tooltipData && tooltipOpen && (
        <TooltipWithBounds
          key={Math.random()}
          top={tooltipTop - 12}
          left={tooltipLeft + margin.left}
          style={{
            ...defaultStyles,
            background: "rgba(0,0,0,0.85)",
            padding: "8px 12px",
            border: "1px solid #333",
            color: "white",
          }}
        >
          <div className="text-sm">
            <div className="font-medium">{tooltipData.date.toLocaleString()}</div>
            <div className="text-blue-400">Count: {tooltipData.count.toLocaleString()}</div>
          </div>
        </TooltipWithBounds>
      )}
    </div>
  );
};

export default Chart;
